import React, { useMemo } from "react";

import { css } from "@emotion/css";
import { CellContext, type SortingFn } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { TelemonitoringTags } from "@/models/CarePlanModel.ts";
import {
  PatientListItem,
  telemonitoringTagColorMapping,
  telemonitoringTagTranslationMapping,
} from "@/models/PatientSummaryModel.ts";
import { userFormattedName } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Tag } from "@/uiKit/atoms/Tag.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";
import { PatientRenewalButton } from "@/uiKit/molecules/CarePlan/PatientRenewalButton.tsx";
import { usePatientListRenewal } from "@/uiKit/organisms/PatientsList/PatientListRenewalState.ts";

type DoctorPatientNameProps = CellContext<PatientListItem, string>;

const selectTagsToDisplay = (tags: TelemonitoringTags[]) => {
  const tagsGenerated = () => {
    const tagsToAdd: TelemonitoringTags[] = [];
    if (tags.includes("nurse_asked_for_reinclusion")) {
      tagsToAdd.push("nurse_asked_for_reinclusion");
    }

    if (tags.includes("expiring_soon")) {
      tagsToAdd.push("expiring_soon");
      return tagsToAdd;
    }
    if (tags.includes("expired_recently")) {
      tagsToAdd.push("expired_recently");
      return tagsToAdd;
    }
    if (tags.includes("no_glycemia_data") && (tags.includes("active") || tags.includes("gestational_no_insulin"))) {
      tagsToAdd.push("no_glycemia_data");
      return tagsToAdd;
    }

    if (tags.includes("expired") && tags.includes("nurse_asked_for_reinclusion")) {
      tagsToAdd.push("expired");
      return tagsToAdd;
    }

    return tagsToAdd;
  };

  return tagsGenerated().sort((a, b) => {
    if (a === "nurse_asked_for_reinclusion") return 1;
    if (b === "nurse_asked_for_reinclusion") return -1;
    return 0;
  });
};

export const DoctorPatientNameColumn: React.FC<DoctorPatientNameProps> = ({ row }) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);
  const { id, familyName, givenName, tags, renewalSuggestion } = row.original;
  const tagsToDisplay: TelemonitoringTags[] = useMemo(() => selectTagsToDisplay(tags), [tags]);
  const hovered = row.isHovered?.();
  const openRenewal = usePatientListRenewal(state => state.openRenewal);

  const showRenewalButton = hovered && renewalSuggestion !== undefined;

  return (
    <div className={styles.container}>
      <Typography className={styles.name} variant="body">
        {userFormattedName(familyName, givenName)}
      </Typography>
      {showRenewalButton && (
        <PatientRenewalButton
          className={styles.renewalButton}
          dataSource="patient-list"
          renewalType={renewalSuggestion}
          onClick={e => {
            e.stopPropagation();
            openRenewal(id, renewalSuggestion);
          }}
        />
      )}
      {!showRenewalButton && (
        <div className={styles.tags}>
          {tagsToDisplay.map(tag => (
            <Tag
              key={`${id}${tag}`}
              label={t(telemonitoringTagTranslationMapping(tag))}
              color={telemonitoringTagColorMapping(tag)}
              icon={tag === "nurse_asked_for_reinclusion" ? <Icons.stethoscope className={styles.icon} /> : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const DoctorPatientNameHeader: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles(makeHeaderStyles);
  return (
    <div className={styles.container}>
      <Typography>{t("pages.patients.columns.patientName")}</Typography>
    </div>
  );
};

export const sortByDoctorPatientName: SortingFn<PatientListItem> = (rowA, rowB) =>
  userFormattedName(rowA.original.familyName, rowA.original.givenName).localeCompare(
    userFormattedName(rowB.original.familyName, rowB.original.givenName),
  );

const makeStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    overflow: "clip",
    whiteSpace: "nowrap",
    padding: theme.spacing(0, 8),
    gap: theme.spacing(8),
  }),
  name: css({
    fontWeight: "bold",
    textWrap: "nowrap",
    minWidth: 0,
    flexShrink: 1,
    overflow: "clip",
    textOverflow: "ellipsis",
  }),
  renewalButton: css({
    maxHeight: theme.spacing(12),
  }),
  tags: css({
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  }),
  icon: css({
    padding: theme.spacing(0.5),
    width: theme.typography.body.fontSize,
    height: theme.typography.body.fontSize,
  }),
});

const makeHeaderStyles = (theme: Theme) => ({
  container: css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    paddingLeft: theme.spacing(8),
  }),
});
